// Autometrics dashboard v3 - measures (spec section 4). Shipment KPIs are at
// LR-record grain; utilization is at trip grain. Ratios are always
// sum / sum, never an average of row-level ratios.

import { OTD_TARGET, CO2_BASELINE, SEVERITY_DEFAULTS } from './amConfig';
import { EXCUSED_CATEGORIES, buildTrips, monthLabel } from './amRules';

export const pct = (n, d) => (d ? (n / d) * 100 : null);

export function shipmentKpis(rows) {
  let delivered = 0; let onTime = 0; let delayed = 0; let excused = 0;
  let ddSum = 0; let ddN = 0; let breachSum = 0;
  const excusedBy = {};
  rows.forEach((r) => {
    if (r.excused) { excused += 1; excusedBy[r.remarkCat] = (excusedBy[r.remarkCat] || 0) + 1; }
    if (r.validDelivery) { ddSum += r.deliveryDays; ddN += 1; }
    if (!r.delivered) return;
    delivered += 1;
    if (r.newPerf === 'On Time') onTime += 1;
    else { delayed += 1; breachSum += r.breachDays || 0; }
  });
  return {
    total: rows.length, delivered, onTime, delayed, excused, excusedBy,
    notDelivered: rows.length - delivered,
    otd: pct(onTime, delivered),
    delayPct: pct(delayed, delivered),
    avgDeliveryDays: ddN ? ddSum / ddN : null,
    avgBreachDays: delayed ? breachSum / delayed : null,
  };
}

export function groupBy(rows, keyFn) {
  const m = new Map();
  rows.forEach((r) => {
    const k = typeof keyFn === 'function' ? keyFn(r) : r[keyFn];
    if (k === null || k === undefined) return;
    let arr = m.get(k);
    if (!arr) { arr = []; m.set(k, arr); }
    arr.push(r);
  });
  return m;
}

/** One stats row per value of `key`, sorted by volume. */
export function groupStats(rows, key) {
  return [...groupBy(rows, key).entries()]
    .map(([name, items]) => ({ name, items, ...shipmentKpis(items) }))
    .sort((a, b) => b.total - a.total);
}

export function sortedMonths(rows) {
  return [...new Set(rows.map((r) => r.month).filter(Boolean))].sort();
}

export function periodKey(r, grain) {
  if (grain === 'year') return r.year;
  if (grain === 'quarter') return r.quarter && `${r.year}-${r.quarter.slice(0, 2)}`;
  return r.month;
}

export function periodLabel(key, grain) {
  if (grain === 'year') return key;
  if (grain === 'quarter') { const [y, q] = key.split('-'); return `${q} ${y}`; }
  return monthLabel(key);
}

/** Adherence vs ETA / CPD / EDD - pure date checks, no excused-remark rule. */
export function adherence(rows, field) {
  let early = 0; let onDate = 0; let late = 0;
  rows.forEach((r) => {
    const v = r[field];
    if (v === 'Early') early += 1; else if (v === 'On date') onDate += 1; else if (v === 'Late') late += 1;
  });
  const n = early + onDate + late;
  return { early, onDate, late, n, pct: pct(early + onDate, n) };
}

/**
 * Top 3 reasons behind the delays that lower OTD: delivered LRs whose New
 * Performance is Delay. Excused delays are On Time, so they are not reasons.
 * Delays with no remark are reported as "No remark given", never hidden.
 */
const topReasons = (items) => {
  const counts = {};
  let delayed = 0;
  items.forEach((r) => {
    if (!r.delivered || r.newPerf !== 'Delay') return;
    delayed += 1;
    const k = r.remarkCat === 'None' ? 'No remark given' : r.remarkCat;
    counts[k] = (counts[k] || 0) + 1;
  });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3)
    .map(([name, count]) => ({ name, count, share: delayed ? (count / delayed) * 100 : null }));
};

/**
 * Top-N attention items (spec page 1). Candidates: volume >= minVolume and
 * (OTD < target or month-on-month drop >= 1 pt). Each component is scaled
 * 0-1 against the largest candidate value, then weighted.
 */
export function severityRanking(rows, key, opts = SEVERITY_DEFAULTS, limit = 5) {
  const months = sortedMonths(rows);
  const last = months[months.length - 1];
  const prev = months[months.length - 2];
  const cands = groupStats(rows, key).filter((g) => g.total >= opts.minVolume).map((g) => {
    const cur = last ? shipmentKpis(g.items.filter((r) => r.month === last)) : null;
    const before = prev ? shipmentKpis(g.items.filter((r) => r.month === prev)) : null;
    const drop = cur && before && cur.otd !== null && before.otd !== null ? before.otd - cur.otd : 0;
    return {
      ...g, gap: Math.max(0, OTD_TARGET - (g.otd ?? OTD_TARGET)), drop: Math.max(0, drop),
      otdBefore: before ? before.otd : null, otdNow: cur ? cur.otd : null,
    };
  }).filter((g) => g.otd !== null && (g.otd < OTD_TARGET || g.drop >= 1));
  if (!cands.length) return [];
  const max = (f) => Math.max(...cands.map(f), 0) || 1;
  const mx = {
    volume: max((g) => g.total), gap: max((g) => g.gap), drop: max((g) => g.drop),
    breach: max((g) => g.avgBreachDays || 0), exceptions: max((g) => g.excused),
  };
  const w = opts.weights;
  const wSum = Object.values(w).reduce((a, b) => a + b, 0) || 1;
  return cands.map((g) => {
    // Each factor's weighted share of the score; the top three become the
    // card's "why flagged" lines (spec: rank, score, volume, OTD, top 3 reasons).
    const parts = {
      volume: (w.volume * g.total) / mx.volume,
      gap: (w.gap * g.gap) / mx.gap,
      drop: (w.drop * g.drop) / mx.drop,
      breach: (w.breach * (g.avgBreachDays || 0)) / mx.breach,
      exceptions: (w.exceptions * g.excused) / mx.exceptions,
    };
    const drivers = Object.entries(parts).filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => k);
    return {
      ...g,
      drivers,
      reasons: topReasons(g.items),
      score: (100 * Object.values(parts).reduce((a, b) => a + b, 0)) / wSum,
    };
  }).sort((a, b) => b.score - a.score).slice(0, limit);
}

/** OTD drops of >= threshold pts between consecutive periods. */
export function otdDrops(series, threshold = 2) {
  const out = [];
  for (let i = 1; i < series.length; i += 1) {
    const a = series[i - 1].otd; const b = series[i].otd;
    if (a !== null && b !== null && a - b >= threshold) out.push({ from: series[i - 1].label, to: series[i].label, drop: a - b, otd: b });
  }
  return out;
}

// ---- FTL utilization (trip grain) ----

export function utilization(trips) {
  let wTrips = 0; let wSum = 0; let wCap = 0; let vTrips = 0; let vSum = 0; let vCap = 0; let weight = 0;
  trips.forEach((t) => {
    weight += t.weight;
    if (t.capKg) { wTrips += 1; wSum += t.weight; wCap += t.capKg; }
    if (t.capCbm && t.cbm !== null) { vTrips += 1; vSum += t.cbm; vCap += t.capCbm; }
  });
  return {
    trips: trips.length, records: trips.reduce((a, t) => a + t.records, 0), weight,
    capTrips: wTrips, capWeight: wCap, utilWeight: wSum, weightUtil: pct(wSum, wCap),
    volTrips: vTrips, volUtil: pct(vSum, vCap),
  };
}

export function ftlTrips(rows) {
  return buildTrips(rows.filter((r) => r.type === 'FTL'));
}

// ---- CO2 ----

/**
 * CO2 totals for in-scope rows, counted as the prototype does: shipments with
 * a distance are counted (n) and add their CO2 and weight; shipments with no
 * distance are reported as missing and left out.
 */
export function co2Stats(rows) {
  let co2 = 0; let weightT = 0; let missing = 0; let n = 0;
  rows.forEach((r) => {
    if (!r.co2Group) return;
    if (r.distKm === null || r.distKm === undefined) { missing += 1; return; }
    n += 1;
    co2 += r.co2 || 0;
    weightT += (r.weight || 0) / 1000;
  });
  return { co2, weightT, missing, n, tpt: tco2PerTonPct(co2, weightT) };
}

/** TCO2/ton % = sum CO2 (t) / sum weight (t), shown as % (1,805 t on ~18,400 t -> 9.8%). */
export function tco2PerTonPct(co2, weightT) {
  return weightT ? (co2 / weightT) * 100 : null;
}

export function co2Group(rows, group, monthsInView, fullYear, baseOverride) {
  const scoped = rows.filter((r) => r.co2Group === group);
  const s = co2Stats(scoped);
  const base = baseOverride || CO2_BASELINE[group];
  const baselineCo2 = fullYear ? base.grossT : (base.grossT * monthsInView) / 12;
  // Nothing computable (no distances mapped): report as unavailable, never as 0 t / 100% reduction.
  if (!s.weightT) {
    return {
      group, actual: null, weightT: null, shipments: 0, missing: s.missing, baseline: baselineCo2, baselineTpt: base.tco2PerTon,
      tpt: null, reduction: null, finalImprovement: null, tptImprovement: null, finalKpi2: null,
    };
  }
  const actualTpt = tco2PerTonPct(s.co2, s.weightT);
  const reduction = pct(baselineCo2 - s.co2, baselineCo2);
  const tptImprovement = actualTpt !== null ? pct(base.tco2PerTon - actualTpt, base.tco2PerTon) : null;
  return {
    group, actual: s.co2, weightT: s.weightT, shipments: s.n, missing: s.missing, baseline: baselineCo2,
    baselineTpt: base.tco2PerTon, tpt: actualTpt, reduction,
    finalImprovement: reduction !== null ? reduction * 0.5 : null,
    tptImprovement, finalKpi2: tptImprovement !== null ? tptImprovement * 0.1 : null,
  };
}

export { EXCUSED_CATEGORIES };
